import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Department, CreateDepartmentRequest, UpdateDepartmentRequest } from '../types/index';
import apiService from '../services/api';
import PageHeader from './ui/PageHeader';
import Badge from './ui/Badge';
import Button from './ui/Button';
import Modal from './ui/Modal';
import EmptyState from './ui/EmptyState';
import { PlusIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';

const Departments: React.FC = () => {
  const { user } = useAuth();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [formData, setFormData] = useState<CreateDepartmentRequest>({
    name: '',
    description: '',
    email: '',
    phone: '',
    managerId: undefined
  });

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getDepartments();
      if (response.success && response.data) {
        setDepartments(response.data);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      // Map managerId to headOfDepartmentId for backend compatibility
      const createData = {
        ...formData,
        headOfDepartmentId: formData.managerId,
      };
      const response = await apiService.createDepartment(createData);
      if (response.success && response.data) {
        setDepartments(prev => [...prev, response.data!]);
        setShowCreateModal(false);
        setFormData({ name: '', description: '', email: '', phone: '', managerId: undefined });
        // Refresh the list to ensure we have the latest data
        await fetchDepartments();
      } else {
        throw new Error(response.error?.message || 'Failed to create department');
      }
    } catch (error) {
      console.error('Error creating department:', error);
      alert(error instanceof Error ? error.message : 'Failed to create department. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDepartment) return;
    
    try {
      const updateData: UpdateDepartmentRequest = {
        name: formData.name,
        description: formData.description,
        managerId: formData.managerId,
      };
      
      const response = await apiService.updateDepartment(selectedDepartment.id, updateData);
      if (response.success && response.data) {
        setDepartments(prev => prev.map(dept => 
          dept.id === selectedDepartment.id ? response.data! : dept
        ));
        setShowEditModal(false);
        setSelectedDepartment(null);
        setFormData({ name: '', description: '', email: '', phone: '', managerId: undefined });
      }
    } catch (error) {
      console.error('Error updating department:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this department?')) return;
    
    try {
      const response = await apiService.deleteDepartment(id);
      if (response.success) {
        setDepartments(prev => prev.filter(dept => dept.id !== id));
      }
    } catch (error) {
      console.error('Error deleting department:', error);
    }
  };

  const openEditModal = (dept: Department) => {
    setSelectedDepartment(dept);
    setFormData({
      name: dept.name,
      description: dept.description,
      email: (dept as any).email || '',
      phone: (dept as any).phone || '',
      managerId: dept.managerId,
    });
    setShowEditModal(true);
  };

  const openCreateModal = () => {
    setFormData({ name: '', description: '', email: '', phone: '', managerId: undefined });
    setShowCreateModal(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Departments"
        subtitle="Manage municipal departments and their staff"
        actions={
          <Button variant="primary" icon={PlusIcon} onClick={openCreateModal}>
            Add Department
          </Button>
        }
      />

      {departments.length === 0 ? (
        <EmptyState icon={BuildingOfficeIcon} title="No departments yet" description="Add a department to start routing reports." />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="table-header">Name</th>
                <th className="table-header">Code</th>
                <th className="table-header">Description</th>
                <th className="table-header">Status</th>
                <th className="table-header text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {departments.map((dept) => (
                <tr key={dept.id} className="hover:bg-gray-50">
                  <td className="table-cell font-medium text-gray-900 dark:text-gray-100">{dept.name}</td>
                  <td className="table-cell text-gray-500 dark:text-gray-400">{dept.name.substring(0, 3).toUpperCase()}</td>
                  <td className="table-cell text-gray-500 dark:text-gray-400">{dept.description}</td>
                  <td className="table-cell">
                    <Badge tone={dept.isActive ? 'success' : 'danger'}>{dept.isActive ? 'Active' : 'Inactive'}</Badge>
                  </td>
                  <td className="table-cell">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="ghost" onClick={() => openEditModal(dept)}>
                        Edit
                      </Button>
                      <Button size="sm" variant="danger-ghost" onClick={() => handleDelete(dept.id)}>
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Department"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button type="submit" form="create-department-form" variant="primary">
              Create
            </Button>
          </>
        }
      >
        <form onSubmit={handleCreate} id="create-department-form" className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
            <input type="text" name="name" value={formData.name} onChange={handleInputChange} className="input-field mt-1" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <textarea name="description" value={formData.description} onChange={handleInputChange} className="input-field mt-1" rows={3} required />
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={showEditModal && !!selectedDepartment}
        onClose={() => setShowEditModal(false)}
        title="Edit Department"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button type="submit" form="edit-department-form" variant="primary">
              Update
            </Button>
          </>
        }
      >
        <form onSubmit={handleEdit} id="edit-department-form" className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
            <input type="text" name="name" value={formData.name} onChange={handleInputChange} className="input-field mt-1" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <textarea name="description" value={formData.description} onChange={handleInputChange} className="input-field mt-1" rows={3} required />
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Departments;
